import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate, daysFromToday } from "@/lib/date";
import { ArrowLeft, MapPin, AlertCircle, User, FileText, Map, MessageSquare, Pencil, Check, X as XIcon, Clock, ShieldCheck, ShieldAlert, Globe, Facebook, CreditCard, RefreshCw, AlertTriangle, Power, PowerOff, BellRing, FolderOpen, LockKeyhole, Calendar, Camera, Loader2, ClipboardList, BadgeCheck, Link2, Sparkles, Rocket, Mail, Inbox, EyeOff } from "lucide-react";
import CareHomeEnquiriesOverlay from "@/components/territory/CareHomeEnquiriesOverlay";
import FranchiseeFilesPanel from "@/components/files/FranchiseeFilesPanel";
import FranchiseePortalControls from "@/components/franchisee/FranchiseePortalControls";
import PortalModulesPanel from "@/components/franchisee/PortalModulesPanel";
import HubPlusStatusPanel from "@/components/franchisee/HubPlusStatusPanel";
import FranchiseeTerritoryWidget from "@/components/territory/FranchiseeTerritoryWidget";
import RecentFilesStrip from "@/components/files/RecentFilesStrip";
import FilePreviewModal from "@/components/files/FilePreviewModal";
import RelinkGoCardlessModal, { PossibleRelinkStrip } from "@/components/RelinkGoCardlessModal";
import CmsContractsPanel from "@/components/franchisee/CmsContractsPanel";
import LaunchChecklistModal from "@/components/LaunchChecklistModal";
import DBSModal from "@/components/dbs/DBSModal";
import RevertConversionModal from "@/components/franchisee/RevertConversionModal";
import FranchiseeDiscoveryPortalPanel from "@/components/franchisee/FranchiseeDiscoveryPortalPanel";
import FranchiseeLaunchPortalPanel from "@/components/franchisee/FranchiseeLaunchPortalPanel";
import { TrainingDaysSection } from "@/components/franchisee/TrainingDaysSection";
import MojoJourney from "@/components/mojo-journey/MojoJourney";
import LoginLog from "@/components/auth/LoginLog";
import AnnouncementReadLog from "@/components/announcements/AnnouncementReadLog";
import FileVaultAuditLog from "@/components/files/FileVaultAuditLog";
import MarketingUsageLog from "@/components/announcements/MarketingUsageLog";
import ReplyWithTemplateModal from "@/components/ReplyWithTemplateModal";
import CorrespondenceModal from "@/components/pipeline/CorrespondenceModal";

// Live GoCardless mandate status pill (read from cached franchisee fields)
const MANDATE_STYLES = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  pending_submission: "bg-blue-100 text-blue-800 border-blue-300",
  submitted: "bg-blue-100 text-blue-800 border-blue-300",
  pending_customer_approval: "bg-amber-100 text-amber-800 border-amber-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  expired: "bg-stone-200 text-stone-700 border-stone-300",
  consumed: "bg-stone-200 text-stone-700 border-stone-300",
  unknown: "bg-stone-100 text-stone-500 border-stone-200",
};
const MANDATE_LABELS = {
  active: "Active",
  pending_submission: "Pending",
  submitted: "Submitted",
  pending_customer_approval: "Awaiting Customer",
  cancelled: "Cancelled",
  failed: "Failed",
  expired: "Expired",
  consumed: "Consumed",
  unknown: "Unknown",
};
// GoCardless dashboard links — clickable everywhere we display a mandate so
// the admin can jump straight to the live record on manage.gocardless.com.
const GOCARDLESS_SIGN_IN = "https://manage.gocardless.com/sign-in";
const gcMandateUrl = (mandateId) =>
  mandateId ? `https://manage.gocardless.com/mandates/${mandateId}` : GOCARDLESS_SIGN_IN;

function MandatePill({ franchisee }) {
  const s = franchisee.gocardless_mandate_status;
  if (!franchisee.gocardless_mandate_id && !s) {
    return (
      <>
        <div className="font-display text-base text-stone-400 mt-1">Not linked</div>
        <a
          href={GOCARDLESS_SIGN_IN}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mandate-setup-link"
          className="text-[10px] text-stone-500 underline hover:text-stone-900 mt-0.5 inline-flex items-center gap-1">
          Set up in GoCardless ↗
        </a>
      </>
    );
  }
  const style = MANDATE_STYLES[s] || MANDATE_STYLES.unknown;
  return (
    <>
      <a
        href={gcMandateUrl(franchisee.gocardless_mandate_id)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mandate-pill"
        title={`Open mandate ${franchisee.gocardless_mandate_id || ""} on GoCardless`}
        className={`inline-block mt-1 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider border rounded-md hover:opacity-80 transition-opacity ${style}`}>
        {MANDATE_LABELS[s] || s || "Unknown"} ↗
      </a>
      {franchisee.gocardless_mandate_reference && (
        <div className="text-[10px] text-stone-500 mt-0.5 tabular-nums truncate">{franchisee.gocardless_mandate_reference}</div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Xero contact pill — mirrors the Mandate pill so the two sit next to each
// other in the KPI strip. Resolves the linked Xero contact on demand via
// `/api/franchisees/{id}/xero-contact-link` (the backend matches by mojo
// email / organisation name and caches the result). One click opens the
// franchisee's contact page directly in Xero.
// ---------------------------------------------------------------------------
function XeroPill({ franchiseeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/franchisees/${franchiseeId}/xero-contact-link`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setData({ status: "error", url: "https://go.xero.com/Contacts/Search/" });
        console.debug("[XeroPill] lookup failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [franchiseeId]);

  if (loading) {
    return <div className="font-display text-base text-stone-300 mt-1">…</div>;
  }
  if (!data || data.status !== "linked") {
    return (
      <>
        <div className="font-display text-base text-stone-400 mt-1">Not linked</div>
        <a
          href={data?.url || "https://go.xero.com/Contacts/Search/"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="xero-setup-link"
          className="text-[10px] text-stone-500 underline hover:text-stone-900 mt-0.5 inline-flex items-center gap-1">
          Find in Xero ↗
        </a>
      </>
    );
  }
  return (
    <>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="xero-pill"
        title={data.contact_name ? `Open ${data.contact_name} on Xero` : "Open contact on Xero"}
        className="inline-block mt-1 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider border rounded-md hover:opacity-80 transition-opacity border-sky-300 bg-sky-50 text-sky-800">
        Open ↗
      </a>
      {data.contact_name && (
        <div className="text-[10px] text-stone-500 mt-0.5 truncate" title={data.contact_name}>
          {data.contact_name}
        </div>
      )}
    </>
  );
}

function Panel({ icon: Icon, title, action, children, testid, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const headerClickable = collapsible;
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={testid}>
      <div
        className={`px-5 py-3 border-b border-stone-200 flex items-center justify-between gap-3 ${headerClickable ? "cursor-pointer select-none hover:brightness-95" : ""}`}
        style={{ backgroundColor: "#eeee84" }}
        onClick={headerClickable ? () => setOpen((v) => !v) : undefined}
        onKeyDown={
          headerClickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpen((v) => !v);
                }
              }
            : undefined
        }
        role={headerClickable ? "button" : undefined}
        tabIndex={headerClickable ? 0 : undefined}
        aria-expanded={collapsible ? open : undefined}
        data-testid={testid ? `${testid}-header` : undefined}
      >
        <div className="flex items-center gap-2 min-w-0">
          {collapsible && (
            <span
              className="w-5 h-5 rounded-full bg-stone-950/10 text-stone-950 flex items-center justify-center shrink-0"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 20 20"
                className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`}
                fill="currentColor"
              >
                <path d="M7 5l6 5-6 5V5z" />
              </svg>
            </span>
          )}
          {Icon && <Icon className="w-3.5 h-3.5 text-stone-950/70 shrink-0" />}
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-950 truncate">{title}</div>
        </div>
        {action && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            {action}
          </div>
        )}
      </div>
      {(!collapsible || open) && <div className="p-5">{children}</div>}
    </div>
  );
}

// Sales pipeline → Launch handoff. Preserves the four-row drawer
// checklist that lived on the contact (Territory confirmed / Contract
// sent / Shadow day + date + with-whom / Training day(s) + dates) so
// the onboarding history isn't lost when the franchisee record takes
// over. Read-only by design — the source of truth was the contact.
function HandoffRow({ label, ticked, children }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span
        className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border ${ticked ? "bg-stone-950 border-stone-950 text-[#dddd16]" : "bg-white border-stone-300 text-transparent"}`}
        aria-hidden="true"
        data-testid={`sales-handoff-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {ticked ? <Check className="w-3 h-3" /> : null}
      </span>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${ticked ? "text-stone-900" : "text-stone-400"}`}>{label}</div>
        {children}
      </div>
    </div>
  );
}

function SalesHandoffPanel({ handoff, salesPack, editing, salesPackOptions, salesPackDraft, setSalesPackDraft, franchiseeId, onTrainingSaved }) {
  const fmt = (d) => {
    if (!d) return null;
    try { return formatDate(d); } catch { return d; }
  };
  return (
    <Panel
      icon={ClipboardList}
      title="Sales Handoff — Onboarding Checklist (from CRM)"
      testid="panel-sales-handoff"
      action={
        handoff.source_checklist_updated_at && (
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-400">
            Last updated {formatDate(handoff.source_checklist_updated_at)}
            {handoff.source_checklist_updated_by ? ` · ${handoff.source_checklist_updated_by}` : ""}
          </div>
        )
      }>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
        <HandoffRow label="Territory confirmed" ticked={!!handoff.territory_defined} />
        <HandoffRow label="Contract sent" ticked={!!handoff.contract_sent} />
        {/* Sales Pack Received — read-through from the source contact so
            the franchisee doesn't carry a duplicate. Read-mode shows
            pack title + sent date (falls back to "— manual entry —"
            when the pack was picked manually and has no true sent
            date, per Paul's brief). Edit-mode shows a dropdown of
            available packs from landing_pages. */}
        {editing ? (
          <HandoffRow label="Sales pack (manual)" ticked={!!salesPackDraft}>
            <select
              value={salesPackDraft || ""}
              onChange={(e) => setSalesPackDraft(e.target.value || null)}
              data-testid="edit-sales-pack"
              className="mt-1 text-xs border border-stone-300 rounded px-2 py-1 bg-white w-full max-w-[360px] focus:outline-none focus:border-stone-900">
              <option value="">— No pack —</option>
              {(salesPackOptions || []).map((p) => (
                <option key={p.slug} value={p.slug}>{p.title || p.slug}</option>
              ))}
            </select>
            <div className="text-[10px] text-stone-400 mt-1">
              Historical fallback — only used when the contact has no auto-stamped pack.
            </div>
          </HandoffRow>
        ) : (salesPack && (
          <HandoffRow label="Sales pack received" ticked>
            <div className="text-xs text-stone-600 mt-0.5">
              <span className="font-medium text-stone-800" data-testid="handoff-sales-pack-title">
                {salesPack.title || salesPack.file_name || salesPack.template_name || "Sales pack"}
              </span>
              {salesPack.sent_at ? (
                <span className="text-stone-500 tabular-nums" data-testid="handoff-sales-pack-sent-at">
                  {" · "}{fmt(salesPack.sent_at)}
                </span>
              ) : salesPack.manual ? (
                <span className="text-stone-400 italic" data-testid="handoff-sales-pack-manual">
                  {" · "}— manual entry —
                </span>
              ) : null}
            </div>
          </HandoffRow>
        ))}
        <HandoffRow label="Shadow day booked" ticked={!!handoff.shadow_day_booked}>
          {(handoff.shadow_day_date || handoff.shadowing_with) && (
            <div className="text-xs text-stone-500 mt-0.5">
              {handoff.shadow_day_date && <span className="tabular-nums">{fmt(handoff.shadow_day_date)}</span>}
              {handoff.shadow_day_date && handoff.shadowing_with && <span> · </span>}
              {handoff.shadowing_with && <span>Shadowing: {handoff.shadowing_with}</span>}
            </div>
          )}
        </HandoffRow>
        {franchiseeId && <TrainingDaysSection franchiseeId={franchiseeId} handoff={handoff} onSaved={onTrainingSaved} />}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-400 mt-3 pt-3 border-t border-stone-100">
        Carried over from the Sales Pipeline on {handoff.captured_at ? formatDate(handoff.captured_at) : "conversion"}
        {handoff.captured_by ? ` by ${handoff.captured_by}` : ""}
      </div>
    </Panel>
  );
}

// In-place editable text field
function EditField({ field, value, label, type = "text", editing, draft, setDraft, mono }) {
  if (!editing) {
    // Emails + phones get tappable href links in read-mode so admins
    // can click through to mail/dial instead of copy-pasting.
    const href = value
      ? (type === "email" ? `mailto:${value}`
        : type === "tel"   ? `tel:${value}`
        : null)
      : null;
    return (
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">{label}</div>
        <div className={`text-sm mt-1 ${mono ? "tabular-nums" : ""}`}>
          {value
            ? (href
                ? <a href={href} className="text-stone-900 hover:text-stone-600 hover:underline break-all" data-testid={`link-${field}`}>{value}</a>
                : <span className="text-stone-900">{value}</span>)
            : <span className="text-stone-300">—</span>}
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">{label}</label>
      <input
        type={type} value={draft[field] ?? ""}
        onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
        data-testid={`edit-${field}`}
        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900" />
    </div>
  );
}

// A big current-contract card with date countdown — used at top of Contracts panel
function CurrentContractCard({ contract }) {
  const renewal = contract.renewal_date;
  const daysLeft = daysFromToday(renewal);
  const tier = daysLeft == null ? null
             : daysLeft < 0 ? "expired"
             : daysLeft <= 30 ? "expiring"
             : daysLeft <= 90 ? "soon"
             : "active";
  const accent = tier === "expired"  ? "border-red-300 bg-red-50"
               : tier === "expiring" ? "border-amber-300 bg-amber-50"
               : tier === "soon"     ? "border-blue-200 bg-blue-50"
               : "border-emerald-200 bg-emerald-50";
  const Icon = tier === "expired" ? ShieldAlert : tier === "expiring" ? Clock : ShieldCheck;
  const accentText = tier === "expired" ? "text-red-700" : tier === "expiring" ? "text-amber-800" : tier === "soon" ? "text-blue-800" : "text-emerald-800";
  return (
    <div className={`border-2 rounded-2xl p-5 ${accent}`} data-testid="current-contract">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accentText}`} />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-700">Current Contract · {contract.ref ? `#${contract.ref}` : "Active"}</div>
        </div>
        <div className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
          tier === "expired" ? "bg-red-200 text-red-900"
          : tier === "expiring" ? "bg-amber-200 text-amber-900"
          : tier === "soon" ? "bg-blue-200 text-blue-900"
          : "bg-emerald-200 text-emerald-900"
        }`}>
          {tier === "expired" ? `Expired ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? "Day" : "Days"} Ago` : daysLeft != null ? `${daysLeft} ${daysLeft === 1 ? "Day" : "Days"} Remaining` : "Active"}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Started</div>
          <div className="font-display text-2xl text-stone-950 tabular-nums mt-0.5">{formatDate(contract.commencement_date)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Renews / Expires</div>
          <div className="font-display text-2xl text-stone-950 tabular-nums mt-0.5">{formatDate(contract.renewal_date)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Term</div>
          <div className="font-display text-2xl text-stone-950 mt-0.5">{contract.contract_term_years ? `${contract.contract_term_years} yrs` : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Monthly Fee</div>
          <div className="font-display text-2xl text-stone-950 mt-0.5">{contract.monthly_fee != null ? `£${contract.monthly_fee}` : "—"}</div>
        </div>
      </div>
    </div>
  );
}

// GoCardless live status + last/next payment panel
function GoCardlessPanel({ franchisee, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [relinkOpen, setRelinkOpen] = useState(false);
  const [possibleRelink, setPossibleRelink] = useState(null);
  const f = franchisee;
  const last = f.gocardless_last_payment;
  const next = f.gocardless_next_payment;
  const linked = !!f.gocardless_mandate_id;

  const handleRefresh = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/gocardless/franchisees/${f.id}/refresh`);
      if (data.linked === false) {
        setErr(data.reason || "No matching GoCardless customer.");
      }
      if (data.franchisee) onRefreshed(data.franchisee);
      setPossibleRelink(data.possible_relink || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not refresh.");
    } finally { setBusy(false); }
  };

  const handleRelinked = (fresh) => {
    onRefreshed(fresh);
    setRelinkOpen(false);
    setPossibleRelink(null);
  };

  return (
    <>
    <Panel
      icon={CreditCard}
      title="GoCardless · Direct Debit"
      testid="panel-gocardless"
      collapsible defaultOpen={false}
      action={
        <div className="flex items-center gap-3">
          <button
            onClick={() => setRelinkOpen(true)}
            data-testid="gc-relink-open"
            className="text-[10px] uppercase tracking-widest font-bold text-stone-500 hover:text-stone-950 flex items-center gap-1"
            title="Search GoCardless by email and relink to a different customer/mandate (e.g. after a bank change)"
          >
            <Link2 className="w-3 h-3" /> Relink Customer
          </button>
          <button onClick={handleRefresh} disabled={busy} data-testid="gc-refresh"
            className="text-[10px] uppercase tracking-widest font-bold text-stone-500 hover:text-stone-950 flex items-center gap-1 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Refreshing…" : "Refresh from GoCardless"}
          </button>
        </div>
      }>
      {!linked ? (
        <div className="text-sm text-stone-500 text-center py-6" data-testid="gc-not-linked">
          Not linked to GoCardless. Run the Sync from the Franchisees page (or click Refresh) to match by email.
        </div>
      ) : (
        <div className="space-y-4">
          {possibleRelink && (
            <PossibleRelinkStrip hint={possibleRelink} franchisee={f} onRelinked={handleRelinked} />
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Status</div>
              <div className="mt-1"><MandatePill franchisee={f} /></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Scheme</div>
              <div className="font-display text-base text-stone-900 mt-1 uppercase">{f.gocardless_mandate_scheme || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Last Payment</div>
              <div className="font-display text-base text-stone-900 mt-1 tabular-nums">
                {last?.amount_str || (last?.amount != null ? `£${(last.amount / 100).toFixed(2)}` : "—")}
              </div>
              <div className="text-[10px] text-stone-500 tabular-nums">{last?.charge_date ? formatDate(last.charge_date) : ""}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Next Payment</div>
              <div className="font-display text-base text-stone-900 mt-1 tabular-nums">
                {next?.amount_str || (next?.amount != null ? `£${(next.amount / 100).toFixed(2)}` : "—")}
              </div>
              <div className="text-[10px] text-stone-500 tabular-nums">{next?.charge_date ? formatDate(next.charge_date) : ""}</div>
            </div>
          </div>
          <div className="text-[10px] text-stone-400 tabular-nums">
            GC Customer: {f.gocardless_customer_id ? (
              <a href={`https://manage.gocardless.com/customers/${f.gocardless_customer_id}`}
                 target="_blank" rel="noopener noreferrer"
                 data-testid="gc-customer-link"
                 className="underline hover:text-stone-900">{f.gocardless_customer_id} ↗</a>
            ) : "—"} · Mandate: {f.gocardless_mandate_id ? (
              <a href={gcMandateUrl(f.gocardless_mandate_id)}
                 target="_blank" rel="noopener noreferrer"
                 data-testid="gc-mandate-link"
                 className="underline hover:text-stone-900">{f.gocardless_mandate_id} ↗</a>
            ) : "—"}
            {f.gocardless_synced_at && <> · Synced {formatDate(f.gocardless_synced_at)}</>}
          </div>
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {err}
            </div>
          )}
        </div>
      )}
    </Panel>
    {relinkOpen && (
      <RelinkGoCardlessModal
        franchisee={f}
        onClose={() => setRelinkOpen(false)}
        onRelinked={handleRelinked}
      />
    )}
    </>
  );
}

export default function FranchiseeDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);
  // Signal-based trigger for the CMS New/Renew modal that lives inside
  // <CmsContractsPanel>. Setting this to a truthy value asks the panel
  // to open its own <NewContractModal> pre-locked to this franchisee.
  const [cmsContractTrigger, setCmsContractTrigger] = useState(0);
  // In-house Launch Prep checklist modal — only available post-conversion,
  // so it lives on the Franchisee page (not on the Contact drawer).
  const [launchOpen, setLaunchOpen] = useState(false);
  // DBS Applications modal — mint a tokenized public form URL and
  // email it to the franchisee. See DBSModal for details.
  const [dbsOpen, setDbsOpen] = useState(false);
  // Revert-conversion modal — admin safety flow to undo an accidental
  // convert-to-franchisee. Only shown when the franchisee record has a
  // ``converted_from_contact_id`` linkage.
  const [revertOpen, setRevertOpen] = useState(false);
  // "Email Franchisee" modal — reuses the same ReplyWithTemplateModal
  // as the Sales & Contacts drawer. Feb 2026: HQ needed a way to send
  // template-based emails to an active franchisee without leaving the
  // franchisee page.
  const [emailOpen, setEmailOpen] = useState(false);
  // Read-through view of the source contact's correspondence timeline.
  // Only meaningful when the franchisee was converted from a contact
  // (``converted_from_contact_id`` present); the button is disabled
  // otherwise. Reuses the exact same modal + endpoints as the Sales
  // Pipeline drawer — the contact record is preserved on conversion,
  // so its email_sends / email_inbounds history stays intact.
  const [correspondenceOpen, setCorrespondenceOpen] = useState(false);
  // Manual Sales Pack picker — hydrated on first Edit click. HQ picks
  // a landing-page slug; the backend routes the write to the source
  // contact (if any) or falls back to the franchisee doc. Options are
  // pre-filtered server-side to packs with a downloadable file_key.
  const [salesPackOptions, setSalesPackOptions] = useState([]);
  const [salesPackDraft, setSalesPackDraft] = useState(null);
  const [salesPackInitial, setSalesPackInitial] = useState(null);
  // "Hide from Dashboard" / "Show on Dashboard" toggle busy flag —
  // HQ-only reversible control that removes this franchisee from the
  // Dashboard's five-slot "New franchisees" overview without touching
  // any other view.
  const [dashHideBusy, setDashHideBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/franchisees/${id}`);
        setData(data);
      } catch (e) { setError("Franchisee not found."); }
      finally { setLoading(false); }
    })();
  }, [id]);

  // Honour a `#section-launch-portal` hash on first paint — the
  // Dashboard "In-Progress Franchisees" journey rows link straight to
  // this anchor, expecting the Discovery & Launch accordion to be
  // open and scrolled into view. Waits for `loading` to flip so the
  // Panel has actually rendered before we try to scroll.
  useEffect(() => {
    if (loading) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#section-launch-portal") return;
    const t = window.setTimeout(() => {
      const el = document.getElementById("section-launch-portal");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [loading]);

  const setFranchisee = (fresh) => setData((d) => ({ ...d, franchisee: fresh }));

  const toggleLifecycle = async (target) => {
    const isDeactivate = target === "ex_franchisee";
    const prompt = isDeactivate
      ? `Deactivate ${data.franchisee.first_name || "this franchisee"}? They'll be tagged EX-Franchisee and excluded from active operations.`
      : `Reactivate ${data.franchisee.first_name || "this franchisee"}? They'll be tagged Franchisee again and you'll be reminded to set up their Direct Debit mandate.`;
    if (!window.confirm(prompt)) return;
    const reason = isDeactivate
      ? (window.prompt("Optional — note the reason for deactivation (visible in audit log):", "") || "").trim()
      : (window.prompt("Optional — note the reason for reactivation:", "") || "").trim();
    setLifecycleBusy(true);
    try {
      const { data: res } = await api.patch(`/franchisees/${id}/lifecycle`, { status: target, reason });
      setFranchisee(res.franchisee);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not change status.");
    } finally { setLifecycleBusy(false); }
  };

  const clearMandateReminder = async () => {
    try {
      await api.post(`/franchisees/${id}/clear-mandate-reminder`);
      setFranchisee({ ...data.franchisee, needs_mandate_setup: false });
    } catch (e) { /* noop */ }
  };

  // Feb-2026 refinement: HQ-only reversible "Hide from Dashboard"
  // control. Removes the franchisee from the Dashboard's five-slot
  // New franchisees overview without touching any other view or the
  // underlying journey data. Clicking again re-includes them.
  const toggleDashboardVisibility = async () => {
    const f = data?.franchisee;
    if (!f) return;
    const nextHide = !f.hide_from_dashboard;
    const label = f.first_name || "this franchisee";
    const prompt = nextHide
      ? `Hide ${label} from the Dashboard's "New franchisees" journey overview?\n\nTheir full journey and admin record stay untouched — this only removes them from the five-slot Dashboard summary.`
      : `Show ${label} on the Dashboard's "New franchisees" journey overview again?`;
    if (!window.confirm(prompt)) return;
    setDashHideBusy(true);
    try {
      const { data: res } = await api.patch(
        `/franchisees/${id}/dashboard-visibility`, { hide: nextHide },
      );
      setFranchisee(res.franchisee);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not update Dashboard visibility.");
    } finally {
      setDashHideBusy(false);
    }
  };

  const handlePhotoPick = () => fileInputRef.current?.click();
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post(`/franchisees/${id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFranchisee({ ...data.franchisee, photos: res.photos, photo_url: res.photo_url });
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not upload photo");
    } finally {
      setPhotoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Open a recent file inside the existing preview modal. We need to add an
  // R2 download URL because the strip rows don't carry one.
  const openRecentFile = async (file) => setPreviewFile(file);
  const downloadRecent = async (key) => {
    try {
      const { data: dl } = await api.get("/files/download", { params: { key, attachment: true } });
      window.location.href = dl.url;
    } catch (err) { alert(err?.response?.data?.detail || "Could not download."); }
  };

  const startEdit = () => {
    const f = data.franchisee;
    setDraft({
      first_name: f.first_name || "", last_name: f.last_name || "",
      organisation: f.organisation || "", email: f.email || "",
      mojo_email: f.mojo_email || "", secondary_email: f.secondary_email || "",
      telephone: f.telephone || "", mobile_phone: f.mobile_phone || "",
      address: f.address || f.address_street || "", city: f.city || "", county: f.county || "", postcode: f.postcode || "",
      country: f.country || "", notes: f.notes || "",
      website: f.website || "", facebook: f.facebook || "",
      bio_url: f.bio_url || "",
      // Admins assign the franchise number (e.g. "0029") on launch —
      // the column on FranchiseesPage shows "—" until this is set.
      franchise_number: f.franchise_number || "",
    });
    // Hydrate the sales-pack picker: fetch options once (cached in
    // component state for the session) and seed the draft with the
    // current pack slug so the dropdown reflects state on open.
    (async () => {
      try {
        if (salesPackOptions.length === 0) {
          const { data } = await api.get("/admin/landing-pages");
          const packs = (data?.items || [])
            .filter((p) => p.file_key && (p.active ?? true))
            .map((p) => ({ slug: p.slug, title: p.title || p.slug, file_name: p.file_name }));
          setSalesPackOptions(packs);
        }
      } catch { /* dropdown just stays empty — non-fatal */ }
    })();
    const currentSlug = f.sales_pack_received?.slug || null;
    setSalesPackDraft(currentSlug);
    setSalesPackInitial(currentSlug);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft({}); setSaveError(""); setSalesPackDraft(null); };
  const saveEdit = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const r = await api.patch(`/franchisees/${id}`, draft);
      let fresh = r.data.franchisee;
      // Only PATCH the sales pack when the user actually changed it —
      // avoids clobbering an auto-stamped contact record with an
      // identical manual write on every save.
      if (salesPackDraft !== salesPackInitial) {
        const packRes = await api.patch(`/franchisees/${id}/sales-pack`, { slug: salesPackDraft });
        fresh = packRes.data.franchisee;
      }
      setData((d) => ({ ...d, franchisee: fresh }));
      setEditing(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail?.message || (typeof detail === "string" ? detail : null);
      setSaveError(msg || "Could not save changes.");
    }
    finally { setSaving(false); }
  };

  const suggestNextFranchiseNumber = async () => {
    setSaveError("");
    try {
      const { data } = await api.get("/admin/franchisees/next-franchise-number");
      if (data?.next) setDraft((d) => ({ ...d, franchise_number: data.next }));
    } catch (e) {
      setSaveError(e?.response?.data?.detail?.message || "Could not fetch next available franchise number.");
    }
  };

  const { current, history } = useMemo(() => {
    if (!data) return { current: null, history: [] };
    // "Current" = the active contract with the latest renewal date that isn't cancelled
    const sorted = [...(data.contracts || [])].sort((a, b) =>
      String(b.renewal_date || "").localeCompare(String(a.renewal_date || "")));
    const cur = sorted.find((c) => !c.cancelled_early) || null;
    return { current: cur, history: sorted.filter((c) => c.id !== cur?.id) };
  }, [data]);

  // Years as franchisee — derived from the EARLIEST contract commencement
  // date on file (so renewals don't reset the clock). Falls back to
  // `date_added` / `created_at` for legacy records without contracts.
  const yearsAsFranchisee = useMemo(() => {
    if (!data) return null;
    const f = data.franchisee || {};
    const starts = (data.contracts || [])
      .map((c) => c.commencement_date)
      .filter(Boolean)
      .sort();
    const earliest = starts[0] || f.date_added || f.created_at || null;
    if (!earliest) return null;
    const start = new Date(earliest);
    if (Number.isNaN(start.getTime())) return null;
    const diff = (Date.now() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    return { years: diff, since: earliest };
  }, [data]);

  // "Bought For" card — canonical initial franchise purchase fee.
  // Single source of truth: ``franchisees.bought_for``. HQ sets this
  // when creating an initial CMS contract (the modal mirrors the value
  // onto the franchisee record) or via the one-shot legacy-tag
  // backfill that runs on backend startup. The card is dashed only
  // when no value has ever been recorded for this franchisee.
  const boughtForDisplay = useMemo(() => {
    if (!data) return null;
    const f = data.franchisee || {};
    const canonical = Number(f.bought_for);
    if (Number.isFinite(canonical) && canonical > 0) {
      return { amount: canonical };
    }
    return null;
  }, [data]);
  const boughtForLabel = boughtForDisplay
    ? (boughtForDisplay.amount >= 1000
        ? `£${(boughtForDisplay.amount / 1000).toFixed(boughtForDisplay.amount % 1000 === 0 ? 0 : 1)}k`
        : `£${boughtForDisplay.amount.toLocaleString("en-GB")}`)
    : null;

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500 text-sm uppercase tracking-widest">Loading…</div>;
  if (error || !data) return (
    <div className="p-12">
      <Link to="/franchisees" className="text-xs uppercase tracking-widest font-bold text-stone-500 hover:text-stone-950">← Back to list</Link>
      <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center gap-2 max-w-lg rounded-xl mt-6">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    </div>
  );

  const f = data.franchisee;
  const contracts = data.contracts || [];
  const territories = data.territories || [];
  const enquiries = data.enquiries || [];
  const photo = f.photos?.[0]?.url;
  const fullName = `${f.first_name || ""} ${f.last_name || ""}`.trim();
  const tags = Array.isArray(f.tags) ? f.tags : f.tags ? [f.tags] : [];
  const statusTag = tags.find((t) => /Franchisee|Licencee|Worldwide|Ex-/i.test(t));
  const feeTag = tags.find((t) => /Mojo Fee|Mojo Live/i.test(t));
  const otherTags = tags.filter((t) => t !== statusTag && t !== feeTag);
  const statusColor = statusTag === "Franchisee" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : statusTag === "Worldwide Licencee" ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                    : statusTag === "EX-Franchisee" ? "bg-stone-100 text-stone-500 border-stone-200"
                    : "bg-stone-50 text-stone-700 border-stone-200";

  return (
    <div className="bg-stone-50 min-h-screen">
      <div className="h-16 border-b border-stone-200 bg-white flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/franchisees" className="text-xs uppercase tracking-widest font-bold text-stone-500 hover:text-stone-950 flex items-center gap-1.5" data-testid="back-to-list">
            <ArrowLeft className="w-3.5 h-3.5" /> Franchisees
          </Link>
          <span className="text-stone-300">·</span>
          <h1 className="font-display text-xl text-stone-950" data-testid="franchisee-detail-name">{fullName || f.organisation || "—"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            (() => {
              // Correspondence sits BEFORE the lifecycle toggle, in the
              // gap the user reserved on the header mock-up. Disabled
              // for franchisees that weren't converted from a contact
              // (seed / direct-add records) — nothing to show.
              const hasContact = !!f.converted_from_contact_id;
              return (
                <button
                  onClick={() => hasContact && setCorrespondenceOpen(true)}
                  disabled={!hasContact}
                  data-testid="franchisee-correspondence-open"
                  title={hasContact
                    ? "View sales-pipeline emails sent to and received from this contact"
                    : "No source contact linked — correspondence unavailable"}
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-900 hover:bg-stone-50 rounded-lg flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Inbox className="w-3.5 h-3.5" /> Correspondence
                </button>
              );
            })()
          )}
          {!editing && (
            (() => {
              const isEx = (f.lifecycle_status === "ex_franchisee") || tags.includes("EX-Franchisee");
              const isLicensee = tags.some((t) => /licencee|licensee/i.test(t));
              const deactivateLabel = isLicensee ? "Make Ex-Licensee" : "Make Ex-Franchisee";
              return isEx ? (
                <button onClick={() => toggleLifecycle("active")} disabled={lifecycleBusy} data-testid="reactivate-franchisee"
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                  <Power className="w-3.5 h-3.5" /> {lifecycleBusy ? "Working…" : "Reactivate"}
                </button>
              ) : (
                <button onClick={() => toggleLifecycle("ex_franchisee")} disabled={lifecycleBusy} data-testid="deactivate-franchisee"
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-red-50 hover:text-red-700 hover:border-red-200 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                  <PowerOff className="w-3.5 h-3.5" /> {lifecycleBusy ? "Working…" : deactivateLabel}
                </button>
              );
            })()
          )}
          {!editing ? (
            <>
              <button
                onClick={() => setEmailOpen(true)}
                data-testid="franchisee-email-open"
                disabled={!(f.email || f.secondary_email)}
                title={
                  (f.email || f.secondary_email)
                    ? "Send a template-based email to this franchisee"
                    : "No email on file for this franchisee"
                }
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-900 hover:bg-stone-50 rounded-lg flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                <Mail className="w-3.5 h-3.5" /> Email Franchisee
              </button>
              <button
                onClick={() => setDbsOpen(true)}
                data-testid="franchisee-dbs-open"
                title="DBS Applications for this franchisee"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-900 hover:bg-stone-50 rounded-lg flex items-center gap-1.5">
                <BadgeCheck className="w-3.5 h-3.5" /> DBS
              </button>
              <button
                onClick={toggleDashboardVisibility}
                disabled={dashHideBusy}
                data-testid={f.hide_from_dashboard ? "show-on-dashboard" : "hide-from-dashboard"}
                title={
                  f.hide_from_dashboard
                    ? "Currently hidden from the Dashboard journey overview — click to make eligible again"
                    : "Remove this franchisee from the Dashboard's five-slot New franchisees overview (full tracker stays visible here)"
                }
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border rounded-lg flex items-center gap-1.5 disabled:opacity-50 ${
                  f.hide_from_dashboard
                    ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                    : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
                }`}>
                <EyeOff className="w-3.5 h-3.5" /> {dashHideBusy ? "Working…" : (f.hide_from_dashboard ? "Show on Dashboard" : "Hide from Dashboard")}
              </button>
              <button
                onClick={() => setLaunchOpen(true)}
                data-testid="franchisee-launch-checklist-open"
                title="In-house launch prep checklist"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 border-stone-950 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> Launch Checklist
                {f.launch_checklist_updated_at && (
                  <span className="text-[10px] font-normal normal-case tracking-normal text-stone-300 ml-1">
                    · {new Date(f.launch_checklist_updated_at).toLocaleDateString("en-GB")}
                  </span>
                )}
              </button>
              <button onClick={startEdit} data-testid="edit-franchisee"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-900 hover:bg-stone-50 rounded-lg flex items-center gap-1.5">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </>
          ) : (
            <>
              <button onClick={cancelEdit} data-testid="cancel-edit"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg flex items-center gap-1.5">
                <XIcon className="w-3.5 h-3.5" /> Cancel
              </button>
              <button onClick={saveEdit} disabled={saving} data-testid="save-edit"
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-8 max-w-[1400px] space-y-6">
        {/* Reactivation reminder — appears when a franchisee was reactivated and their mandate isn't active */}
        {f.needs_mandate_setup && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 flex items-start gap-3" data-testid="mandate-reminder">
            <BellRing className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-900">Set up Direct Debit mandate</div>
              <div className="text-xs text-amber-800 mt-0.5">
                {f.first_name || "This franchisee"} was reactivated{f.reactivated_at ? ` on ${formatDate(f.reactivated_at)}` : ""}. Their previous GoCardless mandate is no longer active. Once you've set up a new mandate at <a className="underline" href="https://manage.gocardless.com" target="_blank" rel="noreferrer">gocardless.com</a>, run the bulk sync from the Franchisees page (or click "Refresh from GoCardless" below) and this reminder will clear automatically.
              </div>
            </div>
            <button onClick={clearMandateReminder} data-testid="dismiss-mandate-reminder"
              className="shrink-0 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 rounded-md">
              Dismiss
            </button>
          </div>
        )}

        {/* HERO — photo on the left, KPI bar on top of the right column,
            then the identity block below it so the long name + address have
            the full row width to breathe (used to be squashed between the
            photo and the KPI tiles). */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 items-start">
          <div className="relative group">
            {photo ? (
              <img src={photo} alt={fullName} className="w-full aspect-square object-cover border border-stone-200 rounded-2xl" />
            ) : (
              <div className="w-full aspect-square bg-stone-100 border border-stone-200 flex items-center justify-center text-5xl font-display text-stone-400 rounded-2xl">
                {(f.first_name?.[0] || "?") + (f.last_name?.[0] || "")}
              </div>
            )}
            {/* Admin-only photo upload overlay */}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload}
              data-testid="photo-input" className="hidden" />
            <button onClick={handlePhotoPick} disabled={photoBusy} data-testid="upload-photo"
              className="absolute bottom-2 right-2 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-stone-950/85 text-white hover:bg-stone-950 rounded-md flex items-center gap-1.5 backdrop-blur-sm disabled:opacity-60">
              {photoBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
              {photoBusy ? "Uploading…" : (photo ? "Replace" : "Upload photo")}
            </button>
          </div>
          <div className="space-y-6 min-w-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
              <div className="bg-white p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Contracts</div>
                <div className="font-display text-2xl text-stone-950 mt-1">{contracts.length}</div>
                <div className="text-xs text-stone-500 mt-0.5">{contracts.filter(c => !c.cancelled_early).length} active</div>
              </div>
              <div className="bg-white p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Territory</div>
                <div className="font-display text-2xl text-stone-950 mt-1">{(f.territory_sectors || []).length || territories.length}</div>
                <div className="text-xs text-stone-500 mt-0.5">postcode sectors</div>
              </div>
              <div className="bg-white p-4" data-testid="kpi-mandate">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Mandate</div>
                <MandatePill franchisee={f} />
              </div>
              <div className="bg-white p-4" data-testid="kpi-xero">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Xero</div>
                <XeroPill franchiseeId={f.id} />
              </div>
              {/* Tenure — "X.X years" since the earliest contract or
                  legacy date_added — replaces the now-removed hero line. */}
              <div className="bg-white p-4" data-testid="kpi-tenure">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Tenure</div>
                {yearsAsFranchisee ? (
                  <>
                    <div className="font-display text-2xl text-stone-950 mt-1 tabular-nums">{yearsAsFranchisee.years.toFixed(1)} yrs</div>
                    <div className="text-xs text-stone-500 mt-0.5">since {formatDate(yearsAsFranchisee.since)}</div>
                  </>
                ) : (
                  <div className="font-display text-2xl text-stone-300 mt-1">—</div>
                )}
              </div>
              {/* Bought For — canonical initial franchise purchase fee
                  (franchisees.bought_for). Single source of truth,
                  set either by HQ on the initial CMS contract modal
                  or by the legacy-tag backfill on backend startup. */}
              <div className="bg-white p-4" data-testid="kpi-bought-for">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Bought For</div>
                {boughtForDisplay != null ? (
                  <>
                    <div className="font-display text-2xl text-stone-950 mt-1 tabular-nums">{boughtForLabel}</div>
                    <div className="text-xs text-stone-500 mt-0.5">Initial franchise fee</div>
                  </>
                ) : (
                  <div className="font-display text-2xl text-stone-300 mt-1">—</div>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {statusTag && <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-md ${statusColor}`}>{statusTag}</span>}
                {feeTag && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#dddd16]/15 border border-[#dddd16]/60 text-stone-900 rounded-md">{feeTag}</span>}
                {f.franchise_number && <span className="text-xs text-stone-500">Franchise #{f.franchise_number}</span>}
              </div>
              <div>
                <h2 className="font-display text-4xl text-stone-950">{fullName || f.organisation}</h2>
                {f.organisation && fullName && <div className="text-base text-stone-600 mt-1">{f.organisation}</div>}
              </div>
              {/* Hero address + tenure rows were removed — those are
                  now surfaced via the KPI strip (Tenure card) and the
                  Address panel which sits directly above Contracts. */}
              {otherTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {otherTags.map((t) => <span key={t} className="px-2 py-0.5 bg-stone-100 text-xs text-stone-700 rounded-md">{t}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact + Address — moved above Contracts at Paul's
            request: this info is what HQ glances at first when they
            open a franchisee page, so it earns the top slot. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="franchisee-detail-block">
          <Panel icon={User} title="Contact Details" testid="panel-contact"
            action={!editing && (
              <button onClick={startEdit} className="text-[10px] uppercase tracking-widest font-bold text-stone-500 hover:text-stone-950 flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}>
            {saveError && editing && (
              <div
                data-testid="franchisee-save-error"
                className="mb-4 border border-red-200 bg-red-50 rounded-lg px-3 py-2 text-sm text-red-800 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{saveError}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-5">
              <div>
                <EditField field="franchise_number" label="Franchise No." value={f.franchise_number} editing={editing} draft={draft} setDraft={setDraft} mono />
                {editing && (
                  <button
                    type="button"
                    onClick={suggestNextFranchiseNumber}
                    data-testid="suggest-next-franchise-number"
                    className="mt-1.5 text-[10px] uppercase tracking-widest font-bold text-stone-500 hover:text-stone-950"
                  >
                    Use next unused number
                  </button>
                )}
              </div>
              <EditField field="organisation" label="Organisation" value={f.organisation} editing={editing} draft={draft} setDraft={setDraft} />
              <EditField field="first_name" label="First Name" value={f.first_name} editing={editing} draft={draft} setDraft={setDraft} />
              <EditField field="last_name" label="Last Name" value={f.last_name} editing={editing} draft={draft} setDraft={setDraft} />
              <EditField field="email" label="Email" value={f.email} type="email" editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="mojo_email" label="Mojo Email" value={f.mojo_email} type="email" editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="secondary_email" label="Secondary Email" value={f.secondary_email} type="email" editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="telephone" label="Telephone" value={f.telephone} type="tel" editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="mobile_phone" label="Mobile" value={f.mobile_phone} type="tel" editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="website" label="Website" value={f.website} editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="facebook" label="Facebook" value={f.facebook} editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="bio_url" label="Mojo biography page" value={f.bio_url} editing={editing} draft={draft} setDraft={setDraft} mono />
            </div>
            {!editing && (f.website || f.facebook) && (
              <div className="flex flex-wrap gap-4 pt-4 mt-4 border-t border-stone-100 text-xs">
                {f.website && <a href={f.website.startsWith("http") ? f.website : `https://${f.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-stone-700 hover:text-stone-950"><Globe className="w-3 h-3" /> {f.website}</a>}
                {f.facebook && <a href={f.facebook.startsWith("http") ? f.facebook : `https://facebook.com/${f.facebook}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-stone-700 hover:text-stone-950"><Facebook className="w-3 h-3" /> {f.facebook}</a>}
              </div>
            )}
          </Panel>

          <Panel icon={MapPin} title="Address" testid="panel-address">
            <div className="grid grid-cols-2 gap-5">
              <EditField field="address" label="Street" value={f.address || f.address_street} editing={editing} draft={draft} setDraft={setDraft} />
              <EditField field="city" label="City" value={f.city} editing={editing} draft={draft} setDraft={setDraft} />
              <EditField field="county" label="County" value={f.county} editing={editing} draft={draft} setDraft={setDraft} />
              <EditField field="postcode" label="Postcode" value={f.postcode} editing={editing} draft={draft} setDraft={setDraft} mono />
              <EditField field="country" label="Country" value={f.country} editing={editing} draft={draft} setDraft={setDraft} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Date Added</div>
                <div className="text-sm text-stone-900 mt-1 tabular-nums">{(f.date_added || f.created_at) ? formatDate(f.date_added || f.created_at) : <span className="text-stone-300">—</span>}</div>
              </div>
            </div>
          </Panel>
        </div>

        {/* CONTRACTS — full-width prominent */}
        {f.sales_handoff && <SalesHandoffPanel handoff={f.sales_handoff} salesPack={f.sales_pack_received}
          franchiseeId={f.id} onTrainingSaved={(h) => setData((d) => ({ ...d, franchisee: { ...d.franchisee, sales_handoff: h } }))}
          editing={editing} salesPackOptions={salesPackOptions}
          salesPackDraft={salesPackDraft} setSalesPackDraft={setSalesPackDraft} />}
        {/* Sales pack was received but no handoff dict exists — happens
            on legacy conversions where ``_extract_sales_handoff``
            didn't run. Fall back to a minimal panel so HQ still sees
            which pack the prospect got before conversion. Also
            renders when a manual pack is being picked in edit mode. */}
        {!f.sales_handoff && (f.sales_pack_received || editing) && (
          <SalesHandoffPanel handoff={{}} salesPack={f.sales_pack_received}
            franchiseeId={f.id} onTrainingSaved={(h) => setData((d) => ({ ...d, franchisee: { ...d.franchisee, sales_handoff: h } }))}
            editing={editing} salesPackOptions={salesPackOptions}
            salesPackDraft={salesPackDraft} setSalesPackDraft={setSalesPackDraft} />
        )}
        {/* Discovery & Launch Portal accordion — always rendered for
            a franchisee record so the Dashboard "New franchisees"
            arrow can deep-link into it. The Discovery sub-panel
            gracefully explains itself when the franchisee has no
            source contact (i.e. was not converted). */}
        {(
          <Panel
            icon={Sparkles}
            title="Discovery & Launch Portal"
            testid="panel-discovery-and-launch-portal"
            collapsible
            defaultOpen={typeof window !== "undefined" && window.location.hash === "#section-launch-portal"}
          >
            <div className="space-y-6">
              <section data-testid="section-discovery-portal" id="section-discovery-portal">
                <div className="flex items-center gap-2 pb-2 mb-3 border-b border-stone-200">
                  <Sparkles className="w-3.5 h-3.5 text-stone-500" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-stone-600">
                    Discovery Portal
                  </div>
                </div>
                <FranchiseeDiscoveryPortalPanel franchisee={f} />
              </section>
              <section
                data-testid="section-launch-portal"
                id="section-launch-portal"
                style={{ scrollMarginTop: "80px" }}
              >
                <div className="flex items-center gap-2 pb-2 mb-3 border-b border-stone-200">
                  <Rocket className="w-3.5 h-3.5 text-stone-500" />
                  <div className="text-[10px] uppercase tracking-widest font-bold text-stone-600">
                    Launch Portal
                  </div>
                </div>
                <div className="mb-4">
                  <MojoJourney journey="franchisee" entityId={f.id} />
                </div>
                <FranchiseeLaunchPortalPanel franchisee={f} />
              </section>
            </div>
          </Panel>
        )}
        <Panel icon={FileText} title={`Contracts (${contracts.length})`} testid="panel-contracts"
          collapsible defaultOpen={false}
          action={
            <button onClick={() => setCmsContractTrigger((n) => n + 1)}
              data-testid="add-contract-btn"
              className="text-[10px] uppercase tracking-widest font-bold text-stone-700 hover:text-stone-950 flex items-center gap-1 px-2.5 py-1 border border-stone-300 hover:bg-stone-50 rounded-md">
              <FileText className="w-3 h-3" /> {contracts.length ? "Add / Renew contract" : "Add first contract"}
            </button>
          }>
          {contracts.length === 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-stone-500 text-center py-3">
                No legacy contracts on file. Use <strong>Add / Renew contract</strong> above
                to create a draft in the central Contracts register.
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">
                  Central Contracts Register
                </div>
                <CmsContractsPanel
                  franchiseeId={f.id}
                  openSignal={cmsContractTrigger}
                  onModalHandled={() => { /* trigger consumed */ }} />
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {current && <CurrentContractCard contract={current} />}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">
                  Central Contracts Register
                </div>
                <CmsContractsPanel
                  franchiseeId={f.id}
                  openSignal={cmsContractTrigger}
                  onModalHandled={() => { /* trigger consumed */ }} />
              </div>
              {history.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Previous Contracts ({history.length})</div>
                  <div className="border border-stone-200 rounded-xl overflow-hidden">
                    <table className="w-full" data-testid="contracts-history">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Ref</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Started</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Ended</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Term</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Fee</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((c) => (
                          <tr key={c.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                            <td className="px-3 py-2 text-sm font-semibold text-stone-700">{c.ref ? `#${c.ref}` : "—"}</td>
                            <td className="px-3 py-2 text-xs text-stone-700 tabular-nums">{formatDate(c.commencement_date)}</td>
                            <td className="px-3 py-2 text-xs text-stone-700 tabular-nums">{formatDate(c.renewal_date)}</td>
                            <td className="px-3 py-2 text-xs text-stone-700">{c.contract_term_years ? `${c.contract_term_years} yrs` : "—"}</td>
                            <td className="px-3 py-2 text-xs text-stone-700">{c.monthly_fee != null ? `£${c.monthly_fee}` : "—"}</td>
                            <td className="px-3 py-2">
                              {c.cancelled_early
                                ? <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 rounded-md">Cancelled</span>
                                : <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-stone-100 text-stone-600 border border-stone-200 rounded-md">Ended</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>

        {/* GoCardless live mandate + payment summary */}
        <GoCardlessPanel franchisee={f} onRefreshed={(fresh) => setData((d) => ({ ...d, franchisee: fresh }))} />

        {/* Files — scoped to this franchisee's R2 folder. Phase-3 portal
            users land here as their primary view. Admins see it inline so
            they don't need to bounce to the global Files menu. */}
        <Panel icon={FolderOpen} title="Files" testid="panel-files" collapsible defaultOpen={false}>
          {/* Live recents — last 30 days of activity scoped to this
              franchisee's own folder + shared brand files. */}
          <RecentFilesStrip
            franchiseeId={f.id}
            onOpenFile={openRecentFile}
            onDownload={downloadRecent}
            onOpenFolder={() => { /* the FranchiseeFilesPanel below is the browser */ }}
          />
          <FranchiseeFilesPanel franchisee={f} />
        </Panel>

        {/* Consolidated PORTAL section (Feb 2026 — Turn 5).
            One panel; three clearly separated subsections:
              (1) Access   — existing FranchiseePortalControls verbatim
                             (unchanged; login enable/disable + reset password)
              (2) Modules  — existing PortalModulesPanel verbatim
                             (unchanged; per-franchisee module toggles + PATCH)
              (3) +HUB Subscription — read-only status/dates/modules
                             (Turn 5 scope: informational only for HQ).
            The two legacy testids (`panel-portal`, `panel-portal-modules`)
            are preserved on the subsection wrappers so any existing
            automation continues to work. */}
        <Panel icon={LockKeyhole} title="Portal" testid="panel-portal-consolidated" collapsible defaultOpen={false}>
          {/* -------- (1) Access -------- */}
          <div className="mb-6" data-testid="panel-portal">
            <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">
              Access
            </div>
            <FranchiseePortalControls
              franchisee={f}
              onChanged={async () => {
                // refresh franchisee data so toggle reflects immediately
                try {
                  const { data } = await api.get(`/franchisees/${f.id}`);
                  // Backend returns {franchisee, contracts, territories,
                  // enquiries}. Unwrap the franchisee — the previous
                  // `franchisee: data` assignment stored the WHOLE
                  // wrapper as the franchisee, which nullified every
                  // top-level email field and caused the "no email on
                  // file" red warning to appear immediately after a
                  // successful portal-login creation (Feb 2026: Sue
                  // Young #0098 regression).
                  setData((d) => ({ ...d, franchisee: data.franchisee }));
                } catch (e) {
                  console.warn("[FranchiseeDetail] post-portal-toggle refresh failed", e);
                }
              }}
            />
          </div>

          {/* -------- (2) Modules -------- */}
          <div className="mb-6 pt-6 border-t border-stone-100" data-testid="panel-portal-modules">
            <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">
              Modules
            </div>
            <PortalModulesPanel
              franchisee={f}
              onChanged={async () => {
                try {
                  const { data } = await api.get(`/franchisees/${f.id}`);
                  setData((d) => ({ ...d, franchisee: data.franchisee }));
                } catch (e) {
                  console.warn("[FranchiseeDetail] post-modules-toggle refresh failed", e);
                }
              }}
            />
          </div>

          {/* -------- (3) +HUB Subscription (read-only) -------- */}
          <div className="pt-6 border-t border-stone-100" data-testid="panel-hub-plus">
            <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">
              +HUB Subscription
            </div>
            <HubPlusStatusPanel franchisee={f} />
          </div>
        </Panel>

        {/* Territory Map — full-width so the map + clients rail +
            Care Groups panel underneath it all get real breathing room.
            Notes moved below (see next Panel). */}
        <Panel
          icon={Map}
          title="Territory Map"
          testid="panel-map"
          collapsible defaultOpen={false}
          action={
            <Link to={`/territory-builder?franchisee_id=${f.id}`}
              data-testid="edit-territory-btn"
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center gap-1.5">
              <Pencil className="w-3 h-3" /> {(f.territory_sectors || []).length ? "Edit territory" : "Set territory"}
            </Link>
          }
        >
          <CareHomeEnquiriesOverlay franchiseeId={f.id}>
            {({ extraPins, hoverId, setHoverId, enabled }) => (
              <FranchiseeTerritoryWidget
                franchiseeId={f.id}
                extraPins={extraPins}
                hoveredExtraPinId={hoverId}
                onExtraPinClick={(id) => setHoverId(id)}
                hideHomeMarkers={enabled}
                adminMode={true}
              />
            )}
          </CareHomeEnquiriesOverlay>
        </Panel>

        <Panel icon={MessageSquare} title="Notes" testid="panel-notes" collapsible defaultOpen={false}>
          {editing ? (
            <textarea value={draft.notes ?? ""} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={6} data-testid="edit-notes"
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900" />
          ) : (
            <div className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed min-h-[60px]">
              {f.notes || <span className="text-stone-300">No notes</span>}
            </div>
          )}
        </Panel>

        {enquiries.length > 0 && (
          <Panel icon={MessageSquare} title={`Original Enquiry (${enquiries.length})`} testid="panel-enquiries">
            <div className="space-y-3">
              {enquiries.slice(0, 5).map((e) => (
                <div key={e.id} className="text-sm border-l-2 border-stone-200 pl-3">
                  <div className="text-xs text-stone-500 tabular-nums">{formatDate(e.date)}</div>
                  <div className="text-stone-900 mt-1">{e.why_contacting || e.message || "—"}</div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {/* Per-franchisee activity & audit log. Lazy-loaded sub-panels;
            each one re-uses the same component as the global Logs page
            but is scoped to THIS franchisee via the `franchiseeId` prop. */}
        <Panel icon={ClipboardList} title="Activity &amp; Logs" testid="panel-activity-logs" collapsible defaultOpen={false}>
          <p className="text-xs text-stone-500 mb-3">
            Everything this franchisee has done in the portal — logins, HQ Updates
            opened, files downloaded, and marketing e-shots they've sent.
          </p>
          <div className="space-y-3">
            <LoginLog franchiseeId={f.id} />
            <AnnouncementReadLog franchiseeId={f.id} />
            <FileVaultAuditLog franchiseeId={f.id} />
            <MarketingUsageLog franchiseeId={f.id} />
          </div>
          {f.converted_from_contact_id && !f.archived_at && (
            <div
              className="mt-6 pt-4 border-t border-stone-200"
              data-testid="revert-conversion-block"
            >
              <div className="text-xs uppercase tracking-widest font-bold text-stone-400 mb-1">
                Admin safety action
              </div>
              <div className="text-xs text-stone-500 leading-relaxed mb-2">
                Converted from a Sales Pipeline contact by mistake? Revert
                this franchisee back to a contact — a preflight will show
                what&apos;s linked before you type REVERT to confirm.
              </div>
              <button
                onClick={() => setRevertOpen(true)}
                data-testid="revert-conversion-open"
                className="text-xs font-bold uppercase tracking-wider text-red-700 hover:text-red-900 underline underline-offset-2"
              >
                Revert to Contact →
              </button>
            </div>
          )}
        </Panel>
      </div>
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
      {dbsOpen && (
        <DBSModal
          franchisee={f}
          onClose={() => setDbsOpen(false)}
        />
      )}
      <LaunchChecklistModal
        open={launchOpen}
        subject={f}
        endpoint={`/franchisees/${f.id}/launch-checklist`}
        onClose={() => setLaunchOpen(false)}
        onSaved={(payload) => {
          // Mirror the latest launch_checklist + audit field back into
          // cached state so the "last updated" label on the button
          // refreshes instantly without re-fetching.
          setData((d) => ({
            ...d,
            franchisee: { ...d.franchisee, ...payload },
          }));
        }}
      />
      {revertOpen && (
        <RevertConversionModal
          franchisee={f}
          onClose={() => setRevertOpen(false)}
          onReverted={() => {
            // The franchisee has been archived or hard-deleted. Bounce
            // the admin back to the Franchisees list — this record is
            // no longer meaningful to view.
            setTimeout(() => {
              window.location.href = "/franchisees";
            }, 1800);
          }}
        />
      )}
      {/* Reuses the same ReplyWithTemplateModal the Sales drawer opens
          — passing ``franchisee`` instead of ``contact`` swings the
          backend send onto the franchisee record, skips the lead-only
          side effects (pipeline auto-advance, follow-up counters), and
          keeps the audit trail on the franchisee page. */}
      <ReplyWithTemplateModal
        open={emailOpen}
        franchisee={emailOpen ? {
          id: f.id,
          first_name: f.first_name,
          last_name: f.last_name,
          email: f.email || f.secondary_email || "",
          email_raw: f.email || f.secondary_email || "",
          source: null,
        } : null}
        onClose={() => setEmailOpen(false)}
        onSent={() => setEmailOpen(false)}
      />
      {/* Correspondence timeline for the source contact. The modal
          calls ``/contacts/{id}/correspondence`` under the hood — we
          hand it the CONTACT id (not the franchisee id) so its
          existing endpoint keeps working. First/last name + email
          come from the franchisee doc so the header shows the right
          person even if the underlying contact row is stale. */}
      {correspondenceOpen && f.converted_from_contact_id && (
        <CorrespondenceModal
          open
          contact={{
            id: f.converted_from_contact_id,
            first_name: f.first_name,
            last_name: f.last_name,
            email: f.email || f.secondary_email,
            telephone: f.telephone || f.mobile_phone,
            postcode: f.postcode,
          }}
          onClose={() => setCorrespondenceOpen(false)}
        />
      )}
    </div>
  );
}
